/**
 * P2P Connection Status Indicator
 *
 * Shows current connection status with expandable details.
 * Includes Bluetooth mesh controls and online/offline indicator.
 */

import { useState, useEffect } from 'react'
import type { P2PStatus, AllowedTransportsConfig } from '@/lib/types'
import { DEFAULT_ALLOWED_TRANSPORTS } from '@/lib/types'
import { clsx } from 'clsx'

interface ConnectionStatusProps {
  status: P2PStatus
  className?: string
  // Bluetooth controls
  bluetoothAvailable?: boolean
  bluetoothEnabled?: boolean
  onEnableBluetooth?: () => Promise<void>
  onDisableBluetooth?: () => void
  onScanBluetooth?: () => Promise<void>
  // LoRA controls (external Meshtastic device)
  loraAvailable?: boolean
  loraEnabled?: boolean
  onEnableLora?: () => Promise<void>
  onDisableLora?: () => void
  // Room's allowed transports (to show which are enabled/disabled by organizer)
  allowedTransports?: AllowedTransportsConfig
}

export function ConnectionStatus({
  status,
  className,
  bluetoothAvailable = false,
  bluetoothEnabled = false,
  onEnableBluetooth,
  onDisableBluetooth,
  onScanBluetooth,
  loraAvailable = false,
  loraEnabled = false,
  onEnableLora,
  onDisableLora,
  allowedTransports = DEFAULT_ALLOWED_TRANSPORTS,
}: ConnectionStatusProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isScanning, setIsScanning] = useState(false)

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const statusColor = {
    connected: 'bg-green-500',
    degraded: 'bg-yellow-500',
    disconnected: 'bg-red-500',
  }[status.status]

  const statusText = {
    connected: 'Connected',
    degraded: 'Degraded',
    disconnected: 'Disconnected',
  }[status.status]

  // Transport icons and labels
  const transportConfig = {
    webrtc: { icon: '🌐', label: 'P2P', color: 'bg-green-500' },
    websocket: { icon: '📡', label: 'Relay', color: 'bg-yellow-500' },
    bluetooth: { icon: '📶', label: 'BT', color: 'bg-blue-500' },
    lora: { icon: '📻', label: 'LoRa', color: 'bg-purple-500' },
  }

  const activeConfig = status.activeTransport
    ? transportConfig[status.activeTransport]
    : { icon: '❌', label: 'None', color: 'bg-red-500' }

  const handleBluetoothToggle = async () => {
    if (bluetoothEnabled) {
      onDisableBluetooth?.()
    } else {
      await onEnableBluetooth?.()
    }
  }

  const handleBluetoothScan = async () => {
    if (isScanning) return
    setIsScanning(true)
    try {
      await onScanBluetooth?.()
    } finally {
      setIsScanning(false)
    }
  }

  return (
    <div className={clsx('relative', className)}>
      {/* Compact indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-full transition-all border border-transparent hover:border-white/10',
          status.status === 'connected' ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20' :
            status.status === 'degraded' ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20' :
              'bg-red-500/10 text-red-500 hover:bg-red-500/20'
        )}
      >
        {/* Online/Offline indicator */}
        {!isOnline && (
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shadow-[0_0_5px_rgba(249,115,22,0.8)]" title="Offline" />
        )}
        {/* Transport icon */}
        <span className="text-xs" title={`Transport: ${activeConfig.label}`}>
          {activeConfig.icon}
        </span>
        <span className={clsx('w-1.5 h-1.5 rounded-full shadow-[0_0_5px_currentColor]', statusColor)} />
        <span className="text-[10px] font-mono font-bold tracking-wider uppercase">
          {activeConfig.label} <span className="opacity-50">|</span> {status.peerCount}
        </span>
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="absolute top-full right-0 mt-2 w-72 p-1 glass-panel z-50 animate-in fade-in zoom-in-95 duration-200">
          <div className="p-3 bg-black/40 rounded-lg">
            <h4 className="text-xs font-bold text-primary mb-3 uppercase tracking-widest border-b border-white/5 pb-2">
              System Status
            </h4>

            <div className="space-y-3 text-xs font-mono">
              {/* Network status */}
              <div className="flex items-center justify-between">
                <span className="text-white/60">NETWORK_LINK</span>
                <span className={clsx(
                  'px-2 py-0.5 rounded text-[10px] font-bold border',
                  isOnline
                    ? 'bg-green-500/10 text-green-400 border-green-500/30'
                    : 'bg-orange-500/10 text-orange-400 border-orange-500/30'
                )}>
                  {isOnline ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>

              {/* Overall status */}
              <div className="flex items-center justify-between">
                <span className="text-white/60">P2P_MESH</span>
                <span className={clsx(
                  'px-2 py-0.5 rounded text-[10px] font-bold border',
                  status.status === 'connected' && 'bg-green-500/10 text-green-400 border-green-500/30',
                  status.status === 'degraded' && 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
                  status.status === 'disconnected' && 'bg-red-500/10 text-red-500 border-red-500/30'
                )}>
                  {statusText.toUpperCase()}
                </span>
              </div>

              {/* Peer count */}
              <div className="flex items-center justify-between">
                <span className="text-white/60">ACTIVE_NODES</span>
                <span className="text-white font-bold">{status.peerCount}</span>
              </div>

              {/* Transport breakdown */}
              <div className="pt-3 border-t border-white/5">
                <div className="text-primary/70 text-[10px] uppercase tracking-wider mb-2">Transport Layer</div>

                <div className="space-y-1">
                  <div className={clsx(
                    'flex items-center justify-between p-2 rounded border transition-colors',
                    status.webrtcConnected
                      ? 'bg-green-500/5 border-green-500/20'
                      : 'bg-white/5 border-transparent'
                  )}>
                    <span className="flex items-center gap-2">
                      <span className="text-sm">🌐</span>
                      <span className="text-white/80">WebRTC</span>
                    </span>
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[9px] font-bold',
                      status.webrtcConnected
                        ? 'text-green-400'
                        : 'text-white/20'
                    )}>
                      {status.webrtcConnected ? 'LINKED' : 'OFF'}
                    </span>
                  </div>

                  <div className={clsx(
                    'flex items-center justify-between p-2 rounded border transition-colors',
                    status.websocketConnected
                      ? 'bg-yellow-500/5 border-yellow-500/20'
                      : 'bg-white/5 border-transparent'
                  )}>
                    <span className="flex items-center gap-2">
                      <span className="text-sm">📡</span>
                      <span className="text-white/80">RELAY</span>
                    </span>
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[9px] font-bold',
                      status.websocketConnected
                        ? 'text-yellow-400'
                        : 'text-white/20'
                    )}>
                      {status.websocketConnected ? 'LINKED' : 'OFF'}
                    </span>
                  </div>

                  <div className={clsx(
                    'flex items-center justify-between p-2 rounded border transition-colors',
                    status.bluetoothConnected
                      ? 'bg-blue-500/5 border-blue-500/20'
                      : 'bg-white/5 border-transparent'
                  )}>
                    <span className="flex items-center gap-2">
                      <span className="text-sm">📶</span>
                      <span className="text-white/80">BT_MESH</span>
                    </span>
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[9px] font-bold',
                      status.bluetoothConnected
                        ? 'text-blue-400'
                        : 'text-white/20'
                    )}>
                      {status.bluetoothConnected ? 'LINKED' : 'OFF'}
                    </span>
                  </div>

                  <div className={clsx(
                    'flex items-center justify-between p-2 rounded border transition-colors',
                    status.loraConnected
                      ? 'bg-purple-500/5 border-purple-500/20'
                      : 'bg-white/5 border-transparent'
                  )}>
                    <span className="flex items-center gap-2">
                      <span className="text-sm">📻</span>
                      <span className="text-white/80">LORA_MESH</span>
                    </span>
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[9px] font-bold',
                      status.loraConnected
                        ? 'text-purple-400'
                        : 'text-white/20'
                    )}>
                      {status.loraConnected ? 'LINKED' : 'OFF'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bluetooth Controls */}
              <div className="pt-3 border-t border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-primary/70 text-[10px] uppercase tracking-wider">Local Mesh (Bluetooth)</span>
                  {!allowedTransports.bluetooth && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">
                      DISABLED
                    </span>
                  )}
                </div>
                {!allowedTransports.bluetooth ? (
                  <div className="p-2 rounded bg-white/5 border border-white/10">
                    <div className="text-[10px] text-white/40 leading-relaxed">
                      <span className="text-red-400">🚫</span> Bluetooth disabled by room organizer.
                    </div>
                  </div>
                ) : bluetoothAvailable ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleBluetoothToggle}
                      className={clsx(
                        'flex-1 px-2 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border',
                        bluetoothEnabled
                          ? 'bg-primary/20 text-primary border-primary/50 shadow-[0_0_10px_rgba(242,13,128,0.2)]'
                          : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white'
                      )}
                    >
                      {bluetoothEnabled ? 'DISABLE BT' : 'ENABLE BT'}
                    </button>
                    {bluetoothEnabled && (
                      <button
                        onClick={handleBluetoothScan}
                        disabled={isScanning}
                        className={clsx(
                          'flex-1 px-2 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border',
                          isScanning
                            ? 'bg-white/5 text-white/30 border-white/5 cursor-not-allowed'
                            : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white'
                        )}
                      >
                        {isScanning ? 'SCANNING...' : 'SCAN PEERS'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="p-2 rounded bg-white/5 border border-white/10">
                    <div className="text-[10px] text-white/40 leading-relaxed">
                      <span className="text-orange-400">⚠</span> Web Bluetooth not available.
                      <br />
                      <span className="text-white/30">Requires Chrome, Edge, or Opera on desktop/Android. Safari does not support Web Bluetooth.</span>
                    </div>
                  </div>
                )}
              </div>

              {/* LoRA Mesh Controls */}
              <div className="pt-3 border-t border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-purple-400/70 text-[10px] uppercase tracking-wider">Long Range (LoRA)</span>
                  {!allowedTransports.lora && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">
                      DISABLED
                    </span>
                  )}
                </div>
                {!allowedTransports.lora ? (
                  <div className="p-2 rounded bg-white/5 border border-white/10">
                    <div className="text-[10px] text-white/40 leading-relaxed">
                      <span className="text-red-400">🚫</span> LoRA disabled by room organizer.
                    </div>
                  </div>
                ) : loraAvailable ? (
                  <div className="flex gap-2">
                    <button
                      onClick={loraEnabled ? onDisableLora : onEnableLora}
                      className={clsx(
                        'flex-1 px-2 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border',
                        loraEnabled
                          ? 'bg-purple-500/20 text-purple-400 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.2)]'
                          : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white'
                      )}
                    >
                      {loraEnabled ? 'DISABLE LORA' : 'ENABLE LORA'}
                    </button>
                  </div>
                ) : (
                  <div className="p-2 rounded bg-white/5 border border-white/10">
                    <div className="text-[10px] text-white/40 leading-relaxed">
                      <span className="text-purple-400">📻</span> LoRA mesh extends range to kilometers.
                      <br />
                      <span className="text-white/30">Requires external Meshtastic device (T-Beam, Heltec V3). Connect via Bluetooth.</span>
                      <br />
                      <a href="/mesh-hardware" className="text-purple-400 hover:text-purple-300 underline">Learn more →</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Connection Type Legend */}
              <div className="pt-3 border-t border-white/5">
                <div className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Connection Layers</div>
                <div className="grid grid-cols-2 gap-1">
                  <div className="flex items-center gap-2 p-1.5 rounded bg-white/5">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-[9px] text-white/60 font-mono">P2P - Direct</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-white/5">
                    <span className="w-2 h-2 rounded-full bg-yellow-500" />
                    <span className="text-[9px] text-white/60 font-mono">RELAY - Fallback</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-white/5">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-[9px] text-white/60 font-mono">BT - Local ~100m</span>
                  </div>
                  <div className="flex items-center gap-2 p-1.5 rounded bg-white/5">
                    <span className="w-2 h-2 rounded-full bg-purple-500" />
                    <span className="text-[9px] text-white/60 font-mono">LoRa - Long ~10km</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
